/**
 * ThemeModeProvider — клей между Jotai-атомами темы и DOM.
 *  1. Слушает `prefers-color-scheme`, держит `systemDarkAtom` актуальным.
 *  2. Вешает/снимает класс `.dark` на <html> по effective-режиму —
 *     shadcn/Tailwind переключают все CSS-переменные по этому классу.
 *
 * Ничего не рендерит, кроме children. Тема меняется без перезагрузки.
 */

import type {ReactNode} from 'react';
import {useEffect} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';

import {effectiveThemeAtom, systemDarkAtom} from '@atoms/theme';

type ThemeModeProviderProps = {
  children: ReactNode;
};

export const ThemeModeProvider = ({children}: ThemeModeProviderProps) => {
  const setSystemDark = useSetAtom(systemDarkAtom);
  const mode = useAtomValue(effectiveThemeAtom);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [setSystemDark]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark');
  }, [mode]);

  return <>{children}</>;
};
